/**
 * The pills a list draws, in one file, so a colour means the same thing on every screen.
 *
 * CORAL MEANS EXACTLY ONE THING here: a gap somebody has to close. Not told yet, nobody has
 * replied, nobody owns it, late, broken. Everything else is quiet, gold or sage.
 *
 * That is why a VISIT'S STATUS PILL IS NEVER CORAL. Cancelled is a decision somebody already
 * recorded and no access is a visit somebody already reported: both are settled facts, and neither
 * is waiting on anybody. When a visit IS waiting on somebody, a SECOND pill says so beside the
 * status, and that one is coral. Two pills, two questions: what happened, and what is owed.
 *
 * `VisitStatusPill` is the ONLY thing in this product that paints a visit's status, so two screens
 * cannot disagree about the colour of one visit. There is no second definition to drift from.
 *
 * The same goes for the other two states a screen paints, a household's and an agreement's. All
 * three tone rules live here now, for the same reason and after the same drift: each had been
 * written out again inside the screen that drew it, and three of the copies said coral.
 */
import type { ReactNode } from 'react';
import {
  VISIT_STATUS_LABELS,
  type AgreementState,
  type ClientStatus,
  type NotificationKind,
  type VisitStatus,
} from '@fk/shared';
import { Pill } from './AppShell.tsx';

/**
 * The tone a state is painted in, and the whole rule is one sentence: a state is a fact, and a
 * fact is not a gap. So none of the three below can return coral, and the type says so.
 *
 * They live together because they are one rule wearing three names. Each of them used to be a
 * private function inside the screen that drew it, and each drift was the same drift: an ended
 * household, an ended agreement and a cancelled visit painted as alarms, so a decision somebody
 * had already recorded read as work nobody had done.
 */
export type StateTone = 'quiet' | 'gold' | 'sage';

/** Never coral. A status is a fact, and a fact is not a gap. */
export function visitStatusTone(status: VisitStatus): StateTone {
  switch (status) {
    case 'done':
      return 'sage';
    case 'confirmed':
    case 'in_progress':
      return 'gold';
    default:
      return 'quiet';
  }
}

export function VisitStatusPill({ status }: { status: VisitStatus }) {
  return <Pill tone={visitStatusTone(status)}>{VISIT_STATUS_LABELS[status]}</Pill>;
}

/**
 * A household, in the book and on its own page. An ended household is a decision somebody already
 * recorded, not a gap anybody has to close, and a paused one is a household somebody is coming
 * back to.
 */
export function clientStatusTone(status: ClientStatus): StateTone {
  switch (status) {
    case 'active':
      return 'sage';
    case 'paused':
      return 'gold';
    default:
      return 'quiet';
  }
}

/**
 * A standing arrangement, in the panel on the household page and in its own header. Every cadence
 * change ends one agreement and opens the next, so ended is the ordinary end of a healthy
 * arrangement and appears beside the live one for good.
 */
export function agreementStateTone(state: AgreementState): StateTone {
  switch (state) {
    case 'active':
      return 'sage';
    case 'paused':
      return 'gold';
    default:
      return 'quiet';
  }
}

/**
 * A NOTIFICATION, and the difference between a thing nobody has read and a thing nobody has fixed.
 *
 * Every unread notification used to be painted coral, which says the opposite of what this
 * interface's own rule says: coral is a gap somebody has to close, and a message nobody has opened
 * is not yet known to be one. The bell in the header has always known that, and paints its count
 * gold on navy for exactly this reason; the list behind the bell disagreed with it.
 *
 * So unread is gold, like the badge, and coral is decided by the KIND rather than by whether
 * anybody has read it. Three of the seven kinds are gaps by their nature and stay gaps after
 * somebody has read them: a promise this business published and then missed, a message the phone
 * system could not deliver, and the ops card disagreeing with the published page. The other four
 * are things that happened, and something happening is not somebody failing.
 */
const GAPS: ReadonlySet<NotificationKind> = new Set<NotificationKind>([
  'promise_breach',
  'handoff_failed',
  'card_mismatch',
]);

export function isGap(kind: NotificationKind): boolean {
  return GAPS.has(kind);
}

/** Coral for a gap, quiet for a fact. Never decided by whether the row has been read. */
export function notificationKindTone(kind: NotificationKind): 'coral' | 'quiet' {
  return isGap(kind) ? 'coral' : 'quiet';
}

/**
 * The coral pill, and the only kind of thing it may say: something nobody has closed. "2 days" on
 * an enquiry nobody answered, "Open" on a broken promise, "Nobody" where an owner should be.
 */
export function GapPill({ children }: { children: ReactNode }) {
  return <Pill tone="coral">{children}</Pill>;
}

/**
 * THERE IS SOMETHING TO READ on this call. Gold, never coral: a voicemail nobody has opened yet is
 * not a gap either of the two screens that draw this can see, and the office list deliberately says
 * only that the words exist. The words themselves are on the household's own page, where whoever
 * reads them is already looking at whose call it was, and the audio never leaves the phone system
 * at all (`erd.md` Part 6 refuses call recording by name).
 *
 * One definition, two importers, so the office list of calls and one household's history cannot
 * come to say the mark differently.
 */
export function TranscriptMark() {
  return <Pill tone="gold">Transcript</Pill>;
}

/**
 * A count. Gold on navy, the one badge colour in this interface, so a number never competes with
 * the coral that means somebody has to act. `label` is what a reader hears when the digits alone
 * would say nothing.
 */
export function Badge({ children, label }: { children: ReactNode; label?: string }) {
  return (
    <span
      aria-label={label}
      className="inline-flex min-h-[18px] min-w-[18px] items-center justify-center rounded-full bg-gold px-1 text-[11px] leading-none font-semibold text-navy"
    >
      {children}
    </span>
  );
}

/** One definition, two importers: the coral pill on a visit that is recorded and not yet told. */
export { NotToldPill } from './VisitBits.tsx';
